import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const navLinks: { to: string; label: string; isRoute?: boolean }[] = [
  { to: "/growth-strategy", label: "Growth Strategy", isRoute: true },
  { to: "/services", label: "Our Services", isRoute: true },
  { to: "/aeo-to-agentic-commerce", label: "AEO to Agentic Commerce", isRoute: true },
  { to: "/meet-us", label: "Meet Us", isRoute: true },
];

const insightsLinks: { to: string; label: string; isRoute?: boolean }[] = [
  { to: "/resources", label: "Resources", isRoute: true },
  { to: "/growth-strategy#industry-applications", label: "Case Studies", isRoute: true },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [mobileInsightsOpen, setMobileInsightsOpen] = useState(false);
  const insightsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const insightsActive = insightsLinks.some((l) => {
    if (!l.isRoute) return false;
    const [path, hash] = l.to.split("#");
    // Only mark active when pathname matches AND (no hash required, or main nav doesn't already own this path)
    if (hash) return false;
    return location.pathname === path;
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (insightsRef.current && !insightsRef.current.contains(e.target as Node)) {
        setInsightsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground">
        Skip to content
      </a>
      <nav aria-label="Main navigation" className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="section-container flex h-14 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo_growagentic.svg" alt="GrowAgentic logo" className="h-10 w-10" />
            <span className="font-mono font-bold tracking-wider text-gradient text-lg">GrowAgentic</span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const active = link.isRoute && location.pathname === link.to;
              const cls = `px-2.5 py-1.5 transition-colors rounded-lg text-sm ${
                active
                  ? "text-foreground bg-muted font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
              }`;
              return link.isRoute ? (
                <Link key={link.to} to={link.to} className={cls} aria-current={active ? "page" : undefined}>
                  {link.label}
                </Link>
              ) : (
                <a key={link.to} href={link.to} className={cls}>
                  {link.label}
                </a>
              );
            })}

            {/* Insights dropdown */}
            <div ref={insightsRef} className="relative">
              <button
                onClick={() => setInsightsOpen(!insightsOpen)}
                className={`flex items-center gap-1 px-2.5 py-1.5 transition-colors rounded-lg text-sm ${
                  insightsActive
                    ? "text-foreground bg-muted font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                }`}
              >
                Insights
                <ChevronDown className={`h-3 w-3 transition-transform ${insightsOpen ? "rotate-180" : ""}`} />
              </button>
              {insightsOpen && (
                <div className="absolute top-full right-0 mt-1 w-56 rounded-lg border border-border bg-background/95 backdrop-blur-xl shadow-lg py-1">
                  {insightsLinks.map((link) => {
                    const [linkPath, linkHash] = link.to.split("#");
                    const active = link.isRoute && location.pathname === linkPath && !linkHash;
                    const cls = `block px-3 py-2 text-xs transition-colors ${
                      active
                        ? "text-foreground bg-muted font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    }`;
                    return link.isRoute ? (
                      <Link key={link.to} to={link.to} className={cls} onClick={() => setInsightsOpen(false)}>
                        {link.label}
                      </Link>
                    ) : (
                      <a key={link.to} href={link.to} className={cls} onClick={() => setInsightsOpen(false)}>
                        {link.label}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className="ml-3 rounded-xl bg-gradient-primary px-3.5 py-1.5 font-semibold text-primary-foreground transition-all hover:opacity-90 text-sm"
            >
              Get in Touch
            </Link>
          </div>

          <button
            className="lg:hidden text-muted-foreground"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl px-6 py-4 space-y-2">
            {navLinks.map((link) => {
              const active = link.isRoute && location.pathname === link.to;
              const cls = `block text-sm py-1 ${
                active ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground"
              }`;
              return link.isRoute ? (
                <Link key={link.to} to={link.to} className={cls} onClick={() => setOpen(false)} aria-current={active ? "page" : undefined}>
                  {link.label}
                </Link>
              ) : (
                <a key={link.to} href={link.to} className={cls} onClick={() => setOpen(false)}>
                  {link.label}
                </a>
              );
            })}
            <button
              onClick={() => setMobileInsightsOpen(!mobileInsightsOpen)}
              className={`flex items-center gap-1 w-full text-left text-sm py-1 ${
                insightsActive ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Insights
              <ChevronDown className={`h-3 w-3 transition-transform ${mobileInsightsOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileInsightsOpen && (
              <div className="pl-4 space-y-1">
                {insightsLinks.map((link) => {
                  const [linkPath, linkHash] = link.to.split("#");
                  const active = link.isRoute && location.pathname === linkPath && !linkHash;
                  const cls = `block text-sm py-1 ${
                    active ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground"
                  }`;
                  return link.isRoute ? (
                    <Link key={link.to} to={link.to} className={cls} onClick={() => setOpen(false)}>
                      {link.label}
                    </Link>
                  ) : (
                    <a key={link.to} href={link.to} className={cls} onClick={() => setOpen(false)}>
                      {link.label}
                    </a>
                  );
                })}
              </div>
            )}
            <Link
              to="/contact"
              className="block rounded-xl bg-gradient-primary px-4 py-2 text-sm font-semibold text-primary-foreground text-center mt-2"
              onClick={() => setOpen(false)}
            >
              Get in Touch
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
